import { useActionState, startTransition } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { emailSchema } from "@src/validation/schemas";
import { Input } from "@src/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@src/components/ui/form";
import { magicLinkAction, type ActionState, type MagicLinkInput } from "../../actions";
import { EXAMPLE_EMAIL } from "@src/constants";
import Link from "next/link";
import { AuthButton, FormAlert, OTPForm, AuthCard } from "../../_components";
import { Button } from "@src/components/ui/button";
import { paths } from "@src/utils";
const magicLinkFormSchema = z.object({
  email: emailSchema
});
type MagicLinkSignInFormProps = {
  redirectPath?: string;
  onSwitchToPassword: () => void;
};
export function MagicLinkSignInForm({
  redirectPath,
  onSwitchToPassword
}: MagicLinkSignInFormProps) {
  const [state, formAction, isPending] = useActionState<ActionState, MagicLinkInput>(magicLinkAction, {
    result: "idle"
  });
  const form = useForm<z.infer<typeof magicLinkFormSchema>>({
    resolver: zodResolver(magicLinkFormSchema),
    defaultValues: {
      email: ""
    }
  });
  function onSubmit(values: z.infer<typeof magicLinkFormSchema>) {
    startTransition(() => {
      formAction({
        email: values.email
      });
    });
  }
  const showOtpForm = state.result === "success" && state.email;
  if (showOtpForm && state.email) {
    return <AuthCard title="Sign in to your account" description="Enter the 6-digit code from your email">
        <div className="space-y-6">
          <div className="p-3 bg-blue-100 text-blue-700 rounded-md text-center text-sm">
            Check your email for a magic link, or enter the 6-digit code below to sign in.
          </div>
          <OTPForm email={state.email} redirect={redirectPath || paths.home} type="magiclink" />
        </div>
      </AuthCard>;
  }
  return <AuthCard title="Sign in to your account" description="Enter your email below to login to your account" footer={<div className="text-sm text-muted-foreground">
          Don&apos;t have an account?{" "}
          <Link href={paths.register} className="text-primary font-medium hover:underline">
            Register
          </Link>
        </div>} data-sentry-element="AuthCard" data-sentry-component="MagicLinkSignInForm" data-sentry-source-file="MagicLinkSignInForm.tsx">
      <Form {...form} data-sentry-element="Form" data-sentry-source-file="MagicLinkSignInForm.tsx">
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6" noValidate>
          <FormField control={form.control} name="email" render={({
          field
        }) => <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input type="email" placeholder={EXAMPLE_EMAIL} {...field} ref={undefined} />
                </FormControl>
                <FormMessage />
              </FormItem>} data-sentry-element="FormField" data-sentry-source-file="MagicLinkSignInForm.tsx" />

          <AuthButton isPending={isPending} text="Send magic link" pendingText="Sending..." data-ph-capture-attribute-action="auth-signin-magic-link-submit" data-sentry-element="AuthButton" data-sentry-source-file="MagicLinkSignInForm.tsx" />

          <FormAlert state={state} isPending={isPending} data-sentry-element="FormAlert" data-sentry-source-file="MagicLinkSignInForm.tsx" />

          <div className="text-center">
            <Button type="button" variant="link" onClick={onSwitchToPassword} className="text-sm" data-ph-capture-attribute-action="auth-switch-to-password" data-sentry-element="Button" data-sentry-source-file="MagicLinkSignInForm.tsx">
              Sign in with password
            </Button>
          </div>
        </form>
      </Form>
    </AuthCard>;
}